import { memo, Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from "react-i18next"
import { PageHeader, PenModal, NewSectionModal, ToggleBox, NewModal, MainContentPaddingTypes, Overlay, SectionModal, NewButtonList, DraggableButton, TableButton, NewSelect } from "../../../../Components"
import { colors, css, fonts, frames, glyphs, images, useMediaQuery } from '../../../../Styles';
import { ButtonTypes, FieldStyles, NewFieldStyles, SectionBasicTypes, SystemTypes } from '../../../../Definitions';
import { useAppDispatch, useAppSelector } from '../../../../Store';
import { Graph_QL, useSection, useSystem, Location, SectionsInfo, useAnimal, AnimalInfo } from '../../../../Services';
import { Pages } from '../../../../Navigation/root';
// import { ViewTypes } from '../../../../Components/LocationFeeder/Pen/pen';
import UploadImage from '../../../../Components/BaseComponents/uploadImage';
import { BluePrint } from '../../../../Assets/Glyphs/bluePrint';
import { clearActiveAnimals } from '../../../../Store/activeStateHandlers/activeAnimalSlice';
import { selectAllAnimals, selectAnimalById, selectByLocations, upsertAnimals } from '../../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice';
import { currentLfViewTypes, ILfContext, LfContext, LfDispatcherT, LfReducer, useLfContext } from '../../../../Components/LocationFeeder/Modals/_Modals/lfPenContext';
import LF_PENLIST from '../../../../Components/LocationFeeder/SubComponents/lfPenList';

// #region | ButtonList Contents and Styles
const getButtonStyles = (index: number, editTable: boolean) => {
  if (editTable && index === 3) {
    return { backgroundColor: colors.greyPrimary, opacity: 1 };
  }
  return { backgroundColor: colors.greySecondary, opacity: 0.5 };
};

const contents = (
  func_add: () => void,
  func_edit: () => void,
  func_cancel: () => void,
  func_save: () => void,
  label_add: string,
  label_edit: string,
  label_cancel: string,
  label_save: string,
  editTable: boolean
): [string[], JSX.Element[], (() => void)[]] => {
  const buttonEntries: { label: string; glyphs: JSX.Element; onClick: () => void; }[] = [];
  
  if (editTable) {
    buttonEntries.push(
      { label: label_save, glyphs: glyphs.Check({}), onClick: func_save },
      { label: label_cancel, glyphs: glyphs.Close({}), onClick: func_cancel }
    );
  }
    
  buttonEntries.push(
    { label: label_add, glyphs: glyphs.Add({}), onClick: func_add },
    { label: label_edit, glyphs: glyphs.Edit({}), onClick: func_edit }
  );

  const labels = buttonEntries.map((b) => b.label);
  const icons = buttonEntries.map((b) => b.glyphs)
  const onclicks = buttonEntries.map((b) => b.onClick);
  return [labels, icons, onclicks];
};
// #endregion

const penContentOptions = [
  { id: 0, label: "animal number" },
  { id: 1, label: "pen name" }
]

export default function FreedaLocationFeeder() {
  const { t } = useTranslation()
  
  const mobileXs = useMediaQuery(`(max-width: 450px)`);
  const [currentViewType, setCurrentViewType] = useState<"TODAY" | "YESTERDAY" | "ATTENTIONS" | "BASIC">("TODAY");
  
  const [activeSection, setActiveSection] = useState<SectionsInfo | undefined>();
  const [activePen, setActivePen] = useState<Location | undefined>();
  // const [animalsInSection, setAnimalsInSection] = useState<AnimalInfo[]>([]);

  const [editTable, setEditTable] = useState(false)
  const [newSectionModal, setNewSectionModal] = useState(false)
  const [bluePrintModal, setBluePrintModal] = useState<boolean>(false)
  const [uploadModal, setUploadModel] = useState<boolean>(false)
  
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { domain, connection } = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm });
    let connection = Graph_QL.create({ domain: farm });
    return { domain, connection };
  }, [farm]);
  
  const { call: getLfSectionsInfo, filteredBySystemId, filteredBySystemIdSectionIndex, getPensFromSectionIdPenIndex } = useSection(connection);
  const { call: getSystemCall, data: systemsState } = useSystem(connection); 
  const { systemId: urlSystemId } = useParams();
  const systemId = Number(urlSystemId)

  // Data gets
  useEffect(() => {
    getSystemCall({ ids: [systemId] });
    getLfSectionsInfo({ ids: [systemId] }, connection);
  }, [connection, domain, systemId])
  
  // System
  const system = useMemo(() => systemsState[domain]?.data.find((system) => system.system_id === systemId), [domain, systemId, systemsState])
  
  // Sections
  const sections = useMemo(() => filteredBySystemId(domain, systemId), [domain, filteredBySystemId, systemId])
    
  // Current Section
  const { index:currentSectionIndex, name:currentSectionName, pens: currentSectionPens } = filteredBySystemIdSectionIndex(domain, systemId, activeSection?.section_id ?? 0)

  const [_state, dispatch] = useReducer<Reducer<ILfContext, LfDispatcherT>>(LfReducer, {
    farm: farm,
    domain: domain,
    connection: connection,
    systemId: systemId,
    sections: sections,
    penContent: 0,
    currentPenViewType: currentLfViewTypes.remainingToday,
    error: { Elementid: 0, errorText: "" }
  } as ILfContext);

  useEffect(() => {
    if (_state.sections && _state.sections.length > 0) return;
      dispatch({ sections: sections });
  }, [_state.sections, sections]);

  // Handle Section Click
  const handleSectionClick = (section_id: Number) => {
    let section = sections.find(s => s.section_id === section_id)
    if (!section || editTable) return;
    return setActiveSection(section);
  }

  // Handle Pen Click
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handlePenClick = useCallback((location_id: Number) => {
    let pen = currentSectionPens.find((pen) => pen.location_id === location_id)
    if (!pen) return;
    return setActivePen(pen)
  },[currentSectionPens])

  // Animals
  const animalsInSection = useAppSelector((state) => {if (currentSectionPens.length) {return selectByLocations(state.graphQlData.animals, currentSectionPens.map((pen) => pen.location_id))}}) 

  const section = useMemo(() => {
    if (!activeSection || !system) return null;
    return <SectionModal sectionId={activeSection.section_id} system={system} pens={currentSectionPens} onPenClick={(location_id: number) => handlePenClick(location_id)} animalsInSection={animalsInSection ?? []} connect={{ connection: connection, domain: domain, farm: farm }} />
  }, [activeSection, animalsInSection, connection, currentSectionPens, domain, farm, handlePenClick, system]);

  if (!systemId) return null;

  const toggleRouter = (viewType: "TODAY" | "YESTERDAY" | "ATTENTIONS") => {
    // Hvis toggle allerede er aktiv -> sluk den, gå til BASIC
    if (currentViewType === viewType) {
      setCurrentViewType("BASIC");
      dispatch({ currentPenViewType: currentLfViewTypes.basic });
      return;
    }
  
    // Ellers skift til den valgte toggle
    setCurrentViewType(viewType);
  
    const dispatchMap = {
      TODAY: currentLfViewTypes.remainingToday,
      YESTERDAY: currentLfViewTypes.remainingYesterday,
      ATTENTIONS: currentLfViewTypes.attention,
    };
  
    dispatch({ currentPenViewType: dispatchMap[viewType] });
  };

  // Current Pen
  const { index: currentPenIndex, name: currentPenName } = getPensFromSectionIdPenIndex(domain, systemId, activeSection?.section_id ?? 0, activePen?.location_id ?? 0)

  // #region | Button List
  const ButtonRow = memo(({ editTable }: { editTable: boolean }) => {
    const [contentsRes, icons, onClicks] = contents(
      () => { setNewSectionModal(true) },
      () => { setEditTable(true); setCurrentViewType("BASIC") },
      () => { setEditTable(false); setCurrentViewType("TODAY") },
      () => { setCurrentViewType("TODAY") }, // TODO SAVE
      t("buttons.add", { defaultValue: "Add" }),
      t("buttons.edit", { defaultValue: "Edit" }),
      t("buttons.cancel", { defaultValue: "Cancel" }),
      t("buttons.save", { defaultValue: "Save" }),
      editTable
    )
      
    return (
      <NewButtonList 
        contents={contentsRes} 
        onClick={(i) => { onClicks[i]?.() }}
        buttonType={ButtonTypes.tableButton}
        startEnhancers={icons}
        disabled={editTable ?[false, false, true, true] : [false, false] }
        overrides={(index) => {
          const { backgroundColor, opacity } = getButtonStyles(index, editTable);
          return {
            baseButton: { backgroundColor: backgroundColor },
            disabledStyles: { opacity: opacity }
          }
        }}
      />
    )
  })
  // #endregion

  return (
    <frames.Page_inner>
        <PageHeader topHeader={system?.name} subHeader={t(`menuHeaders.${Pages.freeda_lf_sections}`, {defaultValue: "sections"})}/>
      <frames._page_inner_content style={{zIndex: editTable ? "9999" : "0"}}>
        <frames._grid_topBtns_outer>
          <frames._grid_topBtns_inner>
            <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary, marginBottom: "5px" }}>{sections.length} {t("locationFeederPages.sections.sections", {defaultValue: "sections"})}</p>
            <ButtonRow editTable={editTable}/>
          </frames._grid_topBtns_inner>
          <frames.Grid_oneRow>
          <LfContext.Provider value={{ state: _state, dispatch}}>
            <frames._grid_oneRow_top>
              <frames._grid_top_toggleBox_container style={{columnGap: mobileXs ? "10px" : "30px"}}>
                <ToggleBox disabled={editTable? true : false} text={t("feed.general.remainingToday", {defaultValue: "remaining today"})} icon={glyphs.TimeToday({size: "17px"})} onChange={() => toggleRouter("TODAY")} isChecked={ currentViewType === "TODAY" ? true : false}/> 
                <ToggleBox disabled={editTable? true : false} text={t("feed.general.remainingYesterday", {defaultValue: "remaining yesterday"})} icon={glyphs.TimeYesterday({size: "17px"})} onChange={() => toggleRouter("YESTERDAY")} isChecked={currentViewType === "YESTERDAY"? true : false}/>
                <ToggleBox disabled={editTable? true : false} text={t("systemControls.general.attentions", {defaultValue: "attentions"})} icon={<div style={{marginTop: "1px"}}><glyphs.Attention size="17px"/></div>} onChange={() => toggleRouter("ATTENTIONS")} isChecked={currentViewType === "ATTENTIONS"? true : false}/>
              </frames._grid_top_toggleBox_container>
              <NewSelect
                options={penContentOptions}
                fieldOverrides={{
                  fieldStyle: NewFieldStyles.greySecondary,
                  fieldWidth: "150px"
                }}
                valueOverrides={{
                  defaultValue: penContentOptions[0]
                }}
                onChange={(value) => { dispatch({penContent: Number(value?.id)})}}
              />
            </frames._grid_oneRow_top>
            
              <frames._grid_oneRow_content style={{justifyContent: mobileXs ? "center" : "start"}}>
                {_state.sections?.map((section, index) => (
                  <div key={index} onClick={() => handleSectionClick(section.section_id)} style={{height: "fit-content", width: "fit-content"}}>
                    <LF_PENLIST
                      key={section.section_id}
                      section={section}
                    />
                  </div>
                ))}
                {(sections.length === 0 || sections === undefined) ? <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>{t("locationFeederPages.sections.noSections", {defaultValue: "No sections found"})}</p> : <></>}
              </frames._grid_oneRow_content>
            
            <TableButton
              content='blueprint'
              disabled={editTable ? true : false}
              onClick={() => setBluePrintModal(true)}
              startEnhancer={BluePrint({})}
              />
              </LfContext.Provider>
          </frames.Grid_oneRow>
        </frames._grid_topBtns_outer>
      </frames._page_inner_content>

      {/* Section and pen Modals */}
      {system && (activePen || activeSection)?
        <NewModal
          header={activePen !== undefined ? `${t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })} ${currentPenName}` : currentSectionName}
          subHeader={activePen !== undefined ? `${system?.name} ${currentSectionName}` : `${system?.name}`}
          overrides={{
            backgroundColor: colors.greyLightTertiary,
            mainContentPadding: MainContentPaddingTypes.primary,
            mainContentScroll: true
          }}
          predefinedButtons={{
            onClose: () => {
              setActiveSection(undefined)
              if (activePen !== undefined) setActivePen(undefined)
            },
            onGoBack: activePen !== undefined ? () => setActivePen(undefined) : undefined,
            onPrev: () => activePen !== undefined
              ? setActivePen(currentSectionPens[(currentPenIndex - 1 + currentSectionPens?.length) % currentSectionPens.length])
              : setActiveSection(sections[(currentSectionIndex - 1 + sections.length) % sections.length]),
            onNext: () => activePen !== undefined
              ? setActivePen(currentSectionPens[(currentPenIndex + 1) % currentSectionPens.length])
              : setActiveSection(sections[(currentSectionIndex + 1 ) % sections.length]),
          }}
          mainContent={
            <div style={{ width: "735px", display: "flex", flexDirection: "column", alignItems: "center" }}>
              {activePen === undefined && activeSection ?
                  // <SectionModal system={system} activeSectionId={activeSection.section_id} pens={currentSectionPens} onPenClick={(location_id: number) => handlePenClick(location_id)} animalsInSection={animalsInSection} connect={{connection: connection, domain: domain, farm: farm }} />
                  <>{section}</>
                : activePen !== null ?
                  <PenModal activePen={activePen} system={system} /> // PEN POPUP
                  : null
              }
            </div>
          }
        />
      : <></>}

      {editTable && <Overlay/>}
      {bluePrintModal && (
        <NewModal
          modalState={true}
          predefinedButtons={{ onClose: () => setBluePrintModal(false) }}
          header={`${system?.name} ${t("locationFeederPages.sections.blueprint", { defaultValue: "blueprint" })}`}
          tabButtons={{
            contents: ["upload"],
            onClick: (i) => {
              if (i === 0) { setUploadModel(true) }
            },
            startEnhancers: [glyphs.Upload({})],
            disabled: NaN,
            active: NaN
          }}
          mainContent={
            <img src={system?.image ? "data:image/jpeg;base64," + system.image : images.systemView} alt={`${system?.name} ${t("locationFeederPages.sections.map", { defaultValue: "map" })}`} style={{ maxWidth: "1000px", borderRadius: "0px 0px 10px 10px", display: "block" }} />
          }
        />
      )}
      {uploadModal && (
        <NewModal
          header={"Add blueprint"}
          modalState={true}
          predefinedButtons={{ onClose: () => setUploadModel(false) }}
          overrides={{
            mainContentPadding: MainContentPaddingTypes.secondary
          }}
          mainContent={
            <UploadImage systemId={systemId} onClose={() => setUploadModel(false)}/>
          }
        />
      )
      }
      {newSectionModal && (
        <NewSectionModal 
          closeModal={() => {setNewSectionModal(false);}}
          systemId={systemId}
          systemType={SystemTypes.freedaLf}
        />
      )}
    </frames.Page_inner>
  )
}